import { useEffect, useRef } from 'react';
import { DockviewReact, type DockviewReadyEvent } from 'dockview-react';
import { panelComponents, type PanelComponentKey, type PanelParams } from './components.js';
import { CustomTab, PanelHeaderRightActions } from './custom-tab.js';
import { useActiveTabId, useAppStore, useTabPanels, type TabId } from './store.js';
import { useDockviewApi } from './dockview-context.js';

interface Disposable {
  dispose: () => void;
}

// Debounce window for dockview layout-change events. Dragging /
// resizing fires the event continuously; coalesce into a single
// store write so localStorage doesn't get hammered.
const LAYOUT_PERSIST_DEBOUNCE_MS = 300;

// The workspace shell — a dockview canvas hosting the active tab's panels.
//
// Layout seeding: on first mount with an empty tab, we install a sensible
// default arrangement (Target List on the left, Identity top-right, Financial
// Snapshot bottom-right). User-driven layout authoring (add panel, save
// layout, default templates) lands in sub-unit D.
//
// Store ↔ dockview sync: today, the store is the source of truth for
// (panelId, component, link state, current selection). Dockview owns layout
// geometry only. We seed dockview to match the store on first render; future
// add/remove will go through the store, which will then call the dockview
// api to materialise the panel.
export function WorkspaceShell() {
  const activeTabId = useActiveTabId();
  // Normalize at render (persisted state can carry a dangling activeTabId —
  // see setActiveTab in store.ts): an id with no tab renders an empty canvas
  // forever, so fall back to the first tab and repair the store so palette /
  // add-panel calls target a real tab again.
  const resolvedTabId = useAppStore((s) =>
    s.activeTabId && s.tabs[s.activeTabId] ? s.activeTabId : (s.tabOrder[0] ?? null),
  );
  useEffect(() => {
    if (resolvedTabId && resolvedTabId !== activeTabId) {
      useAppStore.getState().actions.setActiveTab(resolvedTabId);
    }
  }, [resolvedTabId, activeTabId]);
  // Part of the key so "Reset layout" (which bumps the epoch) tears the canvas
  // down and rebuilds it, re-running the default panel seed.
  const layoutEpoch = useAppStore((s) =>
    resolvedTabId ? (s.layoutEpoch?.[resolvedTabId] ?? 0) : 0,
  );
  return (
    <div style={{ height: '100%', width: '100%', minHeight: 0 }}>
      {resolvedTabId ? (
        <TabCanvas key={`${resolvedTabId}:${layoutEpoch}`} tabId={resolvedTabId} />
      ) : (
        <div style={{ padding: 'var(--space-4)', color: 'var(--fg-muted)' }}>
          No active tab. (This should never happen — the store seeds one on init.)
        </div>
      )}
    </div>
  );
}

function TabCanvas({ tabId }: { tabId: TabId }) {
  const panels = useTabPanels(tabId);
  const addPanel = useAppStore((s) => s.actions.addPanel);
  const setDockviewLayout = useAppStore((s) => s.actions.setDockviewLayout);
  const pruneOrphanPanels = useAppStore((s) => s.actions.pruneOrphanPanels);
  // Shared with the AppShell-level provider — the command palette reads
  // from this same ref to addPanel into the active dockview. Null out
  // on unmount so a tab-switch can't leave the ref pointing at a
  // destroyed dockview instance.
  const apiRef = useDockviewApi();
  // Disposables held in a ref so the TabCanvas-unmount cleanup can
  // tear them down. We collect them in an array because multiple
  // dockview event listeners need to be attached once the api is
  // ready (layout-change + move-panel + add-panel + remove-panel).
  const disposablesRef = useRef<Disposable[]>([]);
  const layoutTimerRef = useRef<number | null>(null);
  // Previous-location map (panel id → 'grid' | 'floating' | 'popout'
  // | 'edge'). dockview's onDidMovePanel only tells us the panel's
  // CURRENT location and the `from` group reference, neither of
  // which reliably distinguishes a same-locality move (drag within
  // the dock) from a cross-locality transition (drag dock → float).
  // We solve it by remembering each panel's last-seen location and
  // comparing in the move handler.
  const previousLocationRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    return () => {
      apiRef.current = null;
      for (const d of disposablesRef.current) d.dispose();
      disposablesRef.current = [];
      previousLocationRef.current.clear();
      if (layoutTimerRef.current !== null) {
        window.clearTimeout(layoutTimerRef.current);
        layoutTimerRef.current = null;
      }
    };
  }, [apiRef]);

  // Per interface-brief.md Section 2 each default tab has its own
  // panel seed:
  //   Landing   — 5-panel morning brief (3-column triage)
  //   Workspace — Screener (left) + Company Overview (centre) +
  //               Watchlist (right)
  //   Help      — single onboarding content panel
  //
  // We seed inside onReady (below) rather than in a useEffect. The
  // useEffect approach raced against dockview's onReady — when the
  // tab was fresh (no persisted layout) onReady saw an empty store
  // and added nothing; the useEffect filled the store moments later
  // but dockview never received the panels. Merging the two paths
  // into a single synchronous onReady removes the race.
  function seedPanelsIfEmpty() {
    const tab = useAppStore.getState().tabs[tabId];
    if (tab && Object.keys(tab.panels).length > 0) return;
    if (tabId === 'tab_landing') {
      // Landing now carries only Pressure — the compound-signal alerts,
      // watchlist-changes, and M&A tracker panels were removed from the UI.
      addPanel(tabId, {
        id: 'pnl_landing_pressure',
        component: 'landing-pressure',
        title: 'Pressure',
      });
    } else if (tabId === 'tab_help') {
      addPanel(tabId, { id: 'pnl_help', component: 'help', title: 'Help' });
    } else if (tabId === 'tab_workspace') {
      // Only the built-in Workspace tab gets the default panel seed.
      // User-created tabs (the "+" button) start empty — the user opens
      // whatever panels they want via the command palette.
      addPanel(tabId, { id: 'pnl_screener', component: 'screener', title: 'Triage' });
      addPanel(tabId, {
        id: 'pnl_company_overview',
        component: 'company-overview',
        title: 'Record',
      });
      addPanel(tabId, { id: 'pnl_watchlist', component: 'watchlist', title: 'Ward' });
    }
  }
  // Keep the `panels` selector alive so the canvas re-renders when the
  // store changes (e.g. user adds a panel via the catalogue later).
  void panels;

  const onReady = (event: DockviewReadyEvent) => {
    apiRef.current = event.api;
    seedPanelsIfEmpty();

    // If we have a previously-saved layout, restore exact positions
    // / sizes / dock-vs-float state from it and skip the seed
    // arrangement entirely. The saved layout is the user's last
    // arrangement; preserving it across reloads is the whole point.
    const savedLayout = useAppStore.getState().tabs[tabId]?.dockviewLayout;
    if (savedLayout) {
      try {
        // fromJSON re-creates every panel referenced in the layout,
        // calling the panelComponents factory with our PanelParams.
        // Cast through unknown — the store keeps the snapshot opaque
        // so it doesn't depend on dockview's type surface.
        event.api.fromJSON(savedLayout as Parameters<typeof event.api.fromJSON>[0]);
        // The restored layout is the truth about what exists. Any store
        // record it didn't re-create is an orphan from a panel closed in
        // an earlier session — drop it. Without this the records
        // accumulate forever (they bloat localStorage, and the
        // seedPanelsIfEmpty guard reads them, so a tab whose panels were
        // all closed would never re-seed and would stay blank).
        pruneOrphanPanels(
          tabId,
          event.api.panels.map((p) => p.id),
        );
        // Still subscribe to layout changes + panel transitions
        // (below) so further user-initiated rearrangement gets
        // persisted and dock/float duplication keeps working.
        subscribeLayout(event.api);
        subscribePanelTransitions(event.api);
        return;
      } catch (err) {
        // Saved layout is incompatible (e.g., a panel-component was
        // removed). Fall through to seed placement.
        console.warn('[hc] failed to restore dockview layout, re-seeding', err);
      }
    }

    // Docked split-grid seed — panels share the canvas and scale
    // with the browser window (dockview's grid uses relative weights
    // so resizing the window flows through to all groups). User can
    // drag a panel out to float it (the 3-dots menu has an explicit
    // Float action), and the palette opens new panels as floating
    // overlays so they're visibly "new". This is the default
    // structured layout; the floating mode is an opt-in.
    const currentPanels = useAppStore.getState().tabs[tabId]?.panels ?? {};
    const placed = new Set<string>();

    function placeDocked(
      p: { id: string; component: string; title: string },
      position?: Parameters<typeof event.api.addPanel>[0]['position'],
    ) {
      // Persisted layouts can outlive a component-key rename or removal
      // (e.g. `rejected-list` after list-panel.tsx was deleted). Skip
      // any panel whose component isn't currently registered — dockview
      // would otherwise throw "Only React.memo(...)…" mid-render and
      // blank the whole tab.
      if (!(p.component in panelComponents)) {
        console.warn('[hc] skipping panel with unknown component', p.id, p.component);
        placed.add(p.id);
        return;
      }
      event.api.addPanel<PanelParams>({
        id: p.id,
        component: p.component as PanelComponentKey,
        title: p.title,
        params: { tabId, panelId: p.id },
        ...(position ? { position } : {}),
      });
      placed.add(p.id);
    }

    if (tabId === 'tab_landing') {
      // Landing now carries only Pressure (other morning-brief panels removed).
      // Place any landing panels present (back-compat: older layouts may still
      // reference the removed ids, but placeDocked skips unknown components).
      const ids = [
        'pnl_landing_pressure',
        'pnl_landing_alerts',
        'pnl_landing_watchlist',
        'pnl_landing_buyer',
      ];
      const got = ids.map((id) => currentPanels[id]).filter((p): p is NonNullable<typeof p> => !!p);
      if (got[0]) placeDocked(got[0]);
      got.slice(1).forEach((p, idx) => {
        placeDocked(p, { referencePanel: got[idx]!.id, direction: 'right' });
      });
    } else if (tabId === 'tab_help') {
      const p = currentPanels['pnl_help'];
      if (p) placeDocked(p);
    } else {
      // Workspace / user-created tab. Screener + Overview + Watchlist
      // across three columns; back-compat seeds (target-list / identity
      // / financial-snapshot) get appended right of those if present.
      const SEED_IDS = [
        'pnl_screener',
        'pnl_company_overview',
        'pnl_watchlist',
        'pnl_target_list',
        'pnl_identity',
        'pnl_financial_snapshot',
      ] as const;
      const order = SEED_IDS.map((id) => currentPanels[id]).filter(
        (p): p is NonNullable<typeof p> => !!p,
      );
      const [first, ...rest] = order;
      if (first) {
        placeDocked(first);
        rest.forEach((p, idx) => {
          const ref = idx === 0 ? first.id : rest[idx - 1]!.id;
          placeDocked(p, { referencePanel: ref, direction: 'right' });
        });
      }
    }

    // Any panel not part of the per-tab seed (palette-opened, future
    // user additions) — append docked to the right of the last placed
    // panel, or as the very first panel if the tab was empty.
    const lastPlaced = Array.from(placed).pop();
    for (const [id, p] of Object.entries(currentPanels)) {
      if (placed.has(id)) continue;
      placeDocked(p, lastPlaced ? { referencePanel: lastPlaced, direction: 'right' } : undefined);
    }

    // Same reconcile as the restore path: placeDocked skips panels whose
    // component is no longer registered, and those would otherwise linger
    // in the store as permanent orphans.
    pruneOrphanPanels(
      tabId,
      event.api.panels.map((p) => p.id),
    );

    subscribeLayout(event.api);
    subscribePanelTransitions(event.api);
  };

  function subscribeLayout(api: DockviewReadyEvent['api']) {
    disposablesRef.current.push(
      api.onDidLayoutChange(() => {
        if (layoutTimerRef.current !== null) window.clearTimeout(layoutTimerRef.current);
        layoutTimerRef.current = window.setTimeout(() => {
          setDockviewLayout(tabId, api.toJSON());
        }, LAYOUT_PERSIST_DEBOUNCE_MS);
      }),
    );
  }

  // "True floating + docking" — handle the two locality transitions
  // a panel can make:
  //
  //   dock → float  (drag tab outside, or ⇱ on a docked group)
  //     dockview moves the panel to floating and collapses the
  //     original docked slot. The user wants the slot preserved, so
  //     we ADD a docked clone (new id, suffix `__cloned_<ts>`,
  //     same component + params) into any existing docked group.
  //
  //   float → dock  (drag floating header back to a docked group)
  //     dockview moves the panel back to docked. Without cleanup
  //     the user ends up with two docked copies of the same panel
  //     (the re-docked original + the clone we made when it was
  //     first floated). We CLOSE every clone whose id starts with
  //     `${panel.id}__cloned_` so the re-dock collapses cleanly
  //     back to one.
  //
  // Same-locality moves (rearrange within the dock, reposition in
  // floating space) don't trigger either branch.
  //
  // To distinguish locality transitions from same-locality moves we
  // keep a `previousLocationRef` map of panelId → location.type.
  // It's seeded from `api.panels` at attach time and kept fresh via
  // onDidAddPanel / onDidRemovePanel / onDidMovePanel.
  function subscribePanelTransitions(api: DockviewReadyEvent['api']) {
    // Snapshot current locations so the first move event has a
    // baseline to compare against. Includes panels restored via
    // fromJSON or added during the seed phase.
    for (const p of api.panels) {
      previousLocationRef.current.set(p.id, p.api.location.type);
    }

    disposablesRef.current.push(
      api.onDidAddPanel((panel) => {
        previousLocationRef.current.set(panel.id, panel.api.location.type);
      }),
    );
    disposablesRef.current.push(
      api.onDidRemovePanel((panel) => {
        previousLocationRef.current.delete(panel.id);
      }),
    );

    disposablesRef.current.push(
      api.onDidMovePanel(({ panel }) => {
        const currentLoc = panel.api.location.type;
        const prevLoc = previousLocationRef.current.get(panel.id);
        previousLocationRef.current.set(panel.id, currentLoc);

        // Skip clones (they bear the __cloned_ marker) — we don't
        // want a clone-of-a-clone chain, and re-docking a clone
        // shouldn't try to close the original.
        if (panel.id.includes('__cloned_')) return;
        if (prevLoc === undefined) return; // never seen → can't compute transition
        if (prevLoc === currentLoc) return; // same locality, just rearranged

        const params = panel.params as PanelParams | undefined;
        if (!params) return;

        if (currentLoc === 'floating') {
          // dock → float: add a docked clone so the original slot
          // doesn't collapse.
          const cloneId = `${panel.id}__cloned_${Date.now()}`;
          const title = panel.api.title ?? '';
          const targetDocked = api.groups.find((g) => g.api.location.type !== 'floating');
          api.addPanel<PanelParams>({
            id: cloneId,
            component: panel.api.component as PanelComponentKey,
            title,
            params: { tabId: params.tabId, panelId: cloneId },
            ...(targetDocked ? { position: { referenceGroup: targetDocked } } : {}),
          });
          useAppStore.getState().actions.addPanel(params.tabId, {
            id: cloneId,
            component: panel.api.component,
            title,
          });
        } else if (prevLoc === 'floating') {
          // float → dock: tidy any docked clones we made when this
          // panel was originally floated.
          const cloneIdPrefix = `${panel.id}__cloned_`;
          for (const otherPanel of [...api.panels]) {
            if (!otherPanel.id.startsWith(cloneIdPrefix)) continue;
            const cloneParams = otherPanel.params as PanelParams | undefined;
            if (cloneParams) {
              useAppStore.getState().actions.removePanel(cloneParams.tabId, cloneParams.panelId);
            }
            otherPanel.api.close();
          }
        }
      }),
    );
  }

  return (
    // 7px padding makes the OUTER canvas frame match the inter-panel gap.
    // Each panel already insets its groupview 3px inside its splitview
    // cell (see .dv-view:has(>.dv-groupview) in tokens.css), so:
    //   • between two panels: 3 (A) + 4 (sash) + 3 (B) = 10px
    //   • outer edge:         7 (this padding) + 3 (leaf inset) = 10px
    // → one uniform 10px gutter everywhere. box-sizing:border-box keeps
    // the padding inside the 100% box so dockview lays out in the inset
    // area; the --surface-elevated background paints the frame colour
    // (same shade as the leaf-inset gaps and the sash, so it all reads
    // as one continuous canvas frame).
    <div
      style={{
        height: '100%',
        width: '100%',
        padding: 7,
        boxSizing: 'border-box',
        background: 'var(--surface-elevated)',
      }}
      data-tab-id={tabId}
    >
      <DockviewReact
        components={panelComponents}
        defaultTabComponent={CustomTab}
        rightHeaderActionsComponent={PanelHeaderRightActions}
        onReady={onReady}
        className="dockview-theme-light"
      />
    </div>
  );
}
